'use client';

import { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import Breadcrumbs from '@/components/ui/breadcrumbs';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    title: 'Ordering',
    items: [
      {
        question: 'How do I place an order?',
        answer:
          'You can order directly through our website by selecting the product, choosing your size and colour, and adding it to your cart. For custom printing or embroidery, request a free quote through our quote page and we\'ll get back to you within 24 hours.',
      },
      {
        question: 'Is there a minimum order quantity?',
        answer:
          'No minimum order for DTG and DTF printing — we happily print a single item. For embroidery, we recommend a minimum of 5 items to keep the per-unit cost reasonable due to the one-time £25 digitising fee. Screen printing typically requires a minimum of 25 pieces.',
      },
      {
        question: 'Can I order samples before placing a bulk order?',
        answer:
          'Yes, we can produce a sample for you to approve before proceeding with a larger order. Sample costs are deducted from the final order total. Contact us to arrange a sample.',
      },
      {
        question: 'How do I get a quote for custom work?',
        answer:
          'Visit our Get a Quote page and fill in the form with your requirements — garment type, quantity, print method, and any artwork details. We\'ll respond with a detailed quote within 24 hours.',
      },
    ],
  },
  {
    title: 'Printing Methods',
    items: [
      {
        question: 'What is DTG printing?',
        answer:
          'Direct-to-Garment (DTG) printing uses specialised inkjet technology to print designs directly onto fabric. It produces photo-quality results with unlimited colours, a soft hand feel, and is ideal for small quantities or complex, multi-colour designs. No minimum order required.',
      },
      {
        question: 'What is DTF printing?',
        answer:
          'Direct-to-Film (DTF) printing transfers your design onto a special film, which is then heat-pressed onto the garment. It works on virtually any fabric type and colour, producing vibrant, durable results. Great for designs that need to pop on dark fabrics.',
      },
      {
        question: 'What is embroidery and when should I choose it?',
        answer:
          'Embroidery stitches your design directly into the fabric using thread, creating a premium, textured finish. It\'s the most durable decoration method and ideal for logos on polo shirts, workwear, jackets, and caps. There is a one-time £25 digitising fee to convert your artwork into an embroidery file.',
      },
      {
        question: 'Which printing method is best for my order?',
        answer:
          'DTG is best for photo-realistic designs and small quantities. DTF is ideal for vibrant prints on any fabric colour. Embroidery is perfect for professional logos and branding that needs to last. Not sure? Contact us and we\'ll recommend the best option for your project.',
      },
    ],
  },
  {
    title: 'Artwork & Design',
    items: [
      {
        question: 'What file formats do you accept?',
        answer:
          'We accept PNG, PDF, SVG, AI, EPS, and PSD files. For best results, provide vector files (SVG, AI, EPS) or high-resolution images at 300 DPI minimum. If you only have a low-resolution file, we can often recreate your artwork for you.',
      },
      {
        question: 'Can you help design my artwork?',
        answer:
          'Yes! If you have an idea but no finished artwork, our team can help bring it to life. Basic design adjustments are included free of charge. For more complex custom designs, we\'ll provide a quote before starting.',
      },
      {
        question: 'What is the embroidery digitising fee?',
        answer:
          'Embroidery requires your artwork to be converted into a stitch file — this is called digitising. There is a one-time £25 fee per design, which you only pay once. The same digitised file can be reused on future orders at no extra cost.',
      },
    ],
  },
  {
    title: 'Pricing',
    items: [
      {
        question: 'Do you offer bulk discounts?',
        answer:
          'Yes, we offer tiered pricing for larger quantities. The more you order, the lower the per-unit cost. Contact us with your quantity and we\'ll provide a tailored quote with the best possible price.',
      },
      {
        question: 'Are there any hidden fees?',
        answer:
          'No hidden fees. The price you see on our website is what you pay, plus delivery. For custom work, your quote will include all costs — garment, printing/embroidery, and any setup fees — before you commit.',
      },
    ],
  },
  {
    title: 'Delivery',
    items: [
      {
        question: 'How long does delivery take?',
        answer:
          'Standard UK delivery takes 3–5 working days (£6.99). Next day delivery is 1 working day (£9.99). Free Local Collection is usually ready in 24 hours from our Birmingham workshop (126 Beeches Rd, B42 2HQ). Custom printed orders may take an additional 2–5 working days for production.',
      },
      {
        question: 'Do you deliver outside the UK?',
        answer:
          'We primarily deliver within the UK. For international orders, please contact us and we\'ll provide a shipping quote based on your location and order size.',
      },
      {
        question: 'Can I collect my order in person?',
        answer:
          'Yes — Free Local Collection is available from our Birmingham workshop at 126 Beeches Rd, B42 2HQ, usually ready within 24 hours. At checkout choose the pickup option (shown as "126, Beeches Road") and we\'ll email you when your order is ready to collect.',
      },
    ],
  },
  {
    title: 'Returns & Issues',
    items: [
      {
        question: 'What is your returns policy?',
        answer:
          'We accept returns within 30 days of delivery for unworn, unprinted items in their original packaging. Custom printed or embroidered items cannot be returned unless they are faulty or incorrect.',
      },
      {
        question: 'What if my order arrives damaged or incorrect?',
        answer:
          'Contact us within 7 days of delivery with photos of the issue. We\'ll arrange a replacement or full refund at no cost to you. Email hello@oktshirt.co.uk or message us on WhatsApp.',
      },
      {
        question: 'Can I cancel or change my order?',
        answer:
          'You can cancel or amend your order before production begins. Once printing or embroidery has started, changes are not possible. Contact us as soon as possible if you need to make changes.',
      },
    ],
  },
];

export default function FAQPage() {
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (id: string) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <div>
      <Breadcrumbs items={[{ label: 'FAQ' }]} />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Everything you need to know about ordering custom printed and embroidered clothing from OK T-Shirt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            {faqCategories.map((category, catIndex) => (
              <motion.div
                key={category.title}
                className={catIndex > 0 ? 'mt-12' : ''}
                {...fadeUp}
              >
                <h2 className="text-xl lg:text-2xl font-bold mb-6">{category.title}</h2>
                <div className="border-t border-[var(--color-border)]">
                  {category.items.map((item) => {
                    const itemId = `${category.title}-${item.question}`;
                    const isOpen = openItems.includes(itemId);

                    return (
                      <div key={itemId} className="border-b border-[var(--color-border)]">
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="w-full flex items-center justify-between py-5 text-left gap-4"
                        >
                          <span className="text-sm lg:text-base font-medium text-[var(--color-text-primary)]">
                            {item.question}
                          </span>
                          <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 flex-shrink-0 text-[var(--color-text-muted)]"
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </motion.svg>
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <p className="pb-5 text-sm lg:text-base text-[var(--color-text-secondary)] leading-relaxed">
                                {item.answer}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="container">
          <div className="max-w-[64rem] mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Still Have Questions?
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 mb-10 leading-relaxed">
              Get in touch and our team will be happy to help. We typically respond within a few hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold bg-white text-black hover:bg-gray-100 transition-all duration-300"
              >
                Contact Us
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <a
                href="https://wa.me/447403343433"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
